// ---------------------------------------------------------------------------
// Dash creatives API client — TanStack Query v5 hooks + fetchers.
//
// Mirrors the HTTP contract from
// `apps/api/src/routes/creative-hosting.ts` (feature #84). Scope: tenant
// is resolved server-side via tenantResolver middleware; the client only
// needs to pick a campaign and optional type filter.
//
// Ownership: OPUS-D16. Only this file + `./CreativesTable.tsx` +
// `../../routes/creatives.index.tsx` are in the agent's lane.
// ---------------------------------------------------------------------------
import { useQuery } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Domain types — kept in sync with the API's rowToView output.
// ---------------------------------------------------------------------------
export const CREATIVE_TYPES = [
  'banner',
  'video',
  'html',
  'native',
  'email',
  'lp',
  'drive_link',
  'other',
] as const;
export type CreativeType = (typeof CREATIVE_TYPES)[number];

export type CreativeView = {
  id: string;
  campaign_id: string;
  label: string;
  type: CreativeType;
  storage_key: string;
  size_bytes: number;
  mime: string;
  created_at: number;
  updated_at: number;
};

export type ListResponse = {
  campaign_id: string;
  count: number;
  items: CreativeView[];
};

// ---------------------------------------------------------------------------
// Config — API base URL. Vite injects `import.meta.env.VITE_API_BASE_URL`
// at build time; defaults to localhost:8787 for local smoke.
// ---------------------------------------------------------------------------
function getApiBase(): string {
  // `import.meta.env` is injected by Vite at build time. In non-Vite
  // contexts (Node test runners) the object may be absent — tolerate both.
  const meta = import.meta as unknown as {
    env?: Record<string, string | undefined>;
  };
  const envBase =
    typeof meta !== 'undefined' && meta.env
      ? meta.env.VITE_API_BASE_URL
      : undefined;
  return envBase ?? 'http://localhost:8787';
}

// ---------------------------------------------------------------------------
// Pure fetchers — exported so tests and the loader can call directly.
// ---------------------------------------------------------------------------
export async function fetchCreatives(
  campaignId: string,
  init?: RequestInit,
): Promise<ListResponse> {
  const url = `${getApiBase()}/api/v1/creative-hosting/campaign/${encodeURIComponent(campaignId)}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: { accept: 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `creatives_fetch_failed status=${res.status} body=${text.slice(0, 200)}`,
    );
  }
  const parsed = (await res.json()) as ListResponse;
  return parsed;
}

// ---------------------------------------------------------------------------
// Helpers — pure, used by the table for rendering.
// ---------------------------------------------------------------------------
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const v = n / 1024 ** i;
  const fixed = v >= 10 || i === 0 ? v.toFixed(0) : v.toFixed(1);
  return `${fixed} ${units[i]}`;
}

export function formatCreatedAt(unixSeconds: number): string {
  if (!Number.isFinite(unixSeconds) || unixSeconds <= 0) return '—';
  const d = new Date(unixSeconds * 1000);
  return d.toISOString().slice(0, 10);
}

export type TypeFilter = CreativeType | 'all';

export function filterByType(
  items: CreativeView[],
  type: TypeFilter,
): CreativeView[] {
  if (type === 'all') return items;
  return items.filter((r) => r.type === type);
}

// ---------------------------------------------------------------------------
// React hook — TanStack Query v5.
// ---------------------------------------------------------------------------
export function useCreatives(campaignId: string) {
  return useQuery<ListResponse>({
    queryKey: ['creatives', campaignId],
    queryFn: () => fetchCreatives(campaignId),
    enabled: campaignId.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}
